import type { DrawerProps } from '@mantine/core';
import type { ReactNode } from 'react';
import { acquireZIndex, releaseZIndex } from '../surfaceZIndex';

type DrawerConfig = Omit<DrawerProps, 'opened' | 'onClose' | 'children'>;

export interface DrawerItem {
  id: string;
  opened: boolean;
  content: ReactNode;
  loading: boolean;
  props: DrawerConfig;
  /** z-index поверхности, выданный общим аллокатором при открытии. */
  zIndex: number;
  onCloseCallbacks: Array<() => void>;
  onOpenInTab?: () => void;
}

export interface DrawerControllerState {
  drawers: DrawerItem[];
}

export class DrawerController {
  private static instance: DrawerController;
  private listeners = new Set<() => void>();
  private drawers = new Map<string, DrawerItem>();
  private state: DrawerControllerState = { drawers: [] };

  private constructor() {}

  static getInstance(): DrawerController {
    if (!DrawerController.instance) {
      DrawerController.instance = new DrawerController();
    }
    return DrawerController.instance;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): DrawerControllerState => this.state;

  private emit(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }

  private updateState(): void {
    const next: DrawerControllerState = {
      drawers: Array.from(this.drawers.values()),
    };

    const prevIds = this.state.drawers
      .map(d => d.id)
      .sort()
      .join(',');
    const nextIds = next.drawers
      .map(d => d.id)
      .sort()
      .join(',');
    if (prevIds === nextIds && this.state.drawers.length === next.drawers.length) {
      const prevOpened = this.state.drawers
        .map(d => `${d.id}:${d.opened}`)
        .sort()
        .join(',');
      const nextOpened = next.drawers
        .map(d => `${d.id}:${d.opened}`)
        .sort()
        .join(',');
      if (prevOpened === nextOpened) {
        return;
      }
    }

    this.state = next;
    this.emit();
  }

  open(
    id: string,
    content: ReactNode,
    props: DrawerConfig,
    onCloseCallbacks: Array<() => void>,
    loading: boolean,
    onOpenInTab?: () => void
  ): void {
    // Новый дравер получает z-index из общего аллокатора; при re-open того же id
    // сохраняем ранее выданный z, чтобы не крутить счётчик повторно.
    const existing = this.drawers.get(id);
    const zIndex = existing?.zIndex ?? acquireZIndex();
    this.drawers.set(id, {
      id,
      opened: true,
      content,
      loading,
      props,
      zIndex,
      onCloseCallbacks,
      onOpenInTab,
    });
    this.updateState();
  }

  close(id: string): void {
    const drawer = this.drawers.get(id);
    if (!drawer) {
      return;
    }

    this.drawers.delete(id);
    releaseZIndex();
    this.updateState();

    // Важно: запускаем onClose-эффекты в следующем task,
    // чтобы визуальное закрытие drawer не блокировалось тяжёлыми callback'ами.
    setTimeout(() => {
      drawer.onCloseCallbacks.forEach(callback => {
        try {
          callback();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Ошибка в drawer onClose callback:', error);
        }
      });
    }, 0);
  }

  update(id: string, updates: Partial<Pick<DrawerItem, 'content' | 'props' | 'loading'>>): void {
    const existing = this.drawers.get(id);
    if (!existing) {
      return;
    }
    this.drawers.set(id, { ...existing, ...updates });
    this.updateState();
  }

  getDrawer(id: string): DrawerItem | undefined {
    return this.drawers.get(id);
  }
}
